import type { PathwayTemplate, TimeOffset } from "@accurx/message-templates";
import { TZDate } from "@date-fns/tz";
import {
    addDays,
    differenceInCalendarDays,
    format,
    isPast,
    set,
} from "date-fns";
import type { ScheduleSendAt } from "domains/message-component/components/Compose/components/ScheduleSend/types/ScheduleSend.types";
import {
    MESSAGE_EMPTY_STATE,
    START_OF_WORK_DAY,
} from "domains/message-component/constants";
import type {
    ComposeStateType,
    MessageEditor,
} from "domains/message-component/reducer.types";
import { convertMessageSettingsToMessageState } from "domains/message-component/utils/convertMessageSettingsToMessageState";
import { getInitialScheduledSendTime } from "domains/message-component/utils/getInitialScheduledSendTime";

/**
 * Converts a timeOffset (relative to today midnight) to a schedule send date and time
 *
 * @remarks
 * This function is used when a pathway template is added.
 * The delay is calculated relative to midnight of the current day.
 * For example, { days: 0, timeOfDay: 8 } means the message will be sent the same day at 8 AM
 * In case the current time is greater than 8 AM, the message would be sent now.
 *
 * @returns the today+timeOffset.days and sets the time to timeOffset.timeOfDay
 */
export const convertTimeOffsetToDateAndTime = (
    timeOffset: TimeOffset,
): Date | null => {
    const now = new TZDate(new Date(), "Europe/London");

    const startDateMidnight = set(now, {
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
    });

    const dateToSend = addDays(startDateMidnight, timeOffset.days);
    const dateAndTimeToSend = set(dateToSend, {
        hours: timeOffset.timeOfDay || START_OF_WORK_DAY,
    });

    return isPast(dateAndTimeToSend) ? null : dateAndTimeToSend;
};

export const PATHWAY_MESSAGE_TODAY_PLACEHOLDER = "*****TODAYS DATE*****";

function getRelativeSendDate(
    timeOffset: TimeOffset,
    relativeToDateTime: string | undefined,
): ScheduleSendAt {
    const sendAtDateTime = convertTimeOffsetToDateAndTime(
        relativeToDateTime
            ? {
                  timeOfDay: timeOffset.timeOfDay,
                  days:
                      timeOffset.days +
                      differenceInCalendarDays(
                          relativeToDateTime,
                          new TZDate(new Date(), "Europe/London"),
                      ),
              }
            : timeOffset,
    )?.toISOString();
    return sendAtDateTime ? { sendAtDateTime, type: "calendar" } : null;
}

/**
 * Calculates the first message schedule based on the time offset.
 * if it's today, it'll check for weekends and bank holidays
 * if not, it'll just return that date
 */
function calculateTheFirstMessageSchedule(
    timeOffset: TimeOffset,
): ScheduleSendAt {
    const sendAtDate = convertTimeOffsetToDateAndTime(timeOffset);
    if (timeOffset.days === 0 || !sendAtDate) {
        return getInitialScheduledSendTime();
    }
    return { type: "calendar", sendAtDateTime: sendAtDate.toISOString() };
}

export const convertPathwaysTemplateToMessagesState = (
    pathwayTemplate: PathwayTemplate,
    state: Pick<ComposeStateType, "originalGreeting" | "originalSignature">,
): MessageEditor[] => {
    const firstMessageDate = calculateTheFirstMessageSchedule(
        pathwayTemplate.messages[0].timeOffset,
    );

    return pathwayTemplate.messages.map(({ timeOffset, ...message }, i) => ({
        ...MESSAGE_EMPTY_STATE,
        ...convertMessageSettingsToMessageState({
            greeting: state.originalGreeting,
            editableBody: message.body.replace(
                PATHWAY_MESSAGE_TODAY_PLACEHOLDER,
                format(new TZDate(new Date(), "Europe/London"), "do MMMM yyyy"),
            ),
            editableSignature: state.originalSignature,
        }),
        editorTitle: message.title,
        sendAt:
            i === 0
                ? firstMessageDate
                : getRelativeSendDate(
                      timeOffset,
                      firstMessageDate?.sendAtDateTime,
                  ),
    }));
};
